import {
  getImageReference,
  getItemReference,
} from "@tiicker/util/lib/contentful/helpers";
import {
  UnansweredOrSkippedLifestyleQuestions,
  UnansweredOrSkippedLifestyleQuestionsByRelatedTickerSymbol,
} from "@tiicker/util/lib/contentful/queries";
import {
  ContentfulContentType,
  LifestyleQuestionDocument,
} from "@tiicker/util/lib/contentful/types";
import { useContentfulEntries } from "source/contentful/provider";
import styles from "./LifestyleQuestion.module.scss";
import Button from "components/core/Button/Button";
import Container from "components/core/Layout/Container/Container";
import Tooltip from "components/Tooltip/Tooltip";

interface Props {
  userId: number;
  excludedQuestionIds: string[];
  tickerSymbol?: string;
  layout?: "grid" | "stack";
  onSelect: (questionId: string, option: string) => void;
  onSkipQuestion: (questionId: string) => void;
}

export const LifestyleQuestion: React.FC<Props> = (props) => {
  const queryParams = props.tickerSymbol
    ? {
        query: UnansweredOrSkippedLifestyleQuestionsByRelatedTickerSymbol(
          props.excludedQuestionIds || [],
          props.tickerSymbol
        ),
      }
    : {
        query: UnansweredOrSkippedLifestyleQuestions(
          props.excludedQuestionIds || []
        ),
      };

  const questionQuery = useContentfulEntries<LifestyleQuestionDocument>(
    ContentfulContentType.LifestyleQuestion,
    { ...queryParams }
  );

  if (
    questionQuery.state !== "DONE" ||
    questionQuery.result.items.length === 0
  ) {
    return null;
  }

  const layout =
    props.layout && props.layout === "stack"
      ? styles.lifestyleQuestion__stack
      : styles.lifestyleQuestion__grid;

  const unansweredQuestions = questionQuery.result.items.filter(
    (q) => !props.excludedQuestionIds.includes(q.sys.id)
  );

  const thankYouElement = <h2>Thank you for your feedback!</h2>;

  if (unansweredQuestions.length === 0) {
    return (
      <div className={styles.lifestyleQuestion}>
        <div className="container">
          <div className={styles.lifestyleQuestion__thankYou}>
            {thankYouElement}
          </div>
        </div>
      </div>
    );
  }

  const nextQuestion = unansweredQuestions[0];

  const sponsoredCompany = getItemReference(
    nextQuestion.fields.sponsoredBrand,
    {}
  );
  const sponsoredLogoUrl = getImageReference(
    sponsoredCompany?.fields.companyLogoDark
  )?.fields.file.url;

  return (
    <div className={styles.lifestyleQuestion}>
      <Container>
        <div className={layout}>
          <div className={styles.lifestyleQuestion__questionDetailsArea}>
            <span className={styles.lifestyleQuestion__customizeYourExperience}>
              Customize Your Experience
            </span>
            <h2 className={styles.lifestyleQuestion__title}>
              {nextQuestion.fields.questionText}
            </h2>
            {props.layout !== "stack" && (
              <div className={styles.lifestyleQuestion__skipQuestion}>
                <a
                  className={styles.lifestyleQuestion__skipQuestion__link}
                  onClick={() => props.onSkipQuestion(nextQuestion.sys.id)}
                >
                  Skip this question
                </a>
                <span className={styles.lifestyleQuestion__skipQuestion__help}>
                  Why am I seeing this?
                  <Tooltip
                    tooltipText={
                      "Investing is personal. By providing TiiCKER with information about yourself, we can find companies that most strongly align with you, in industries that interest you."
                    }
                  />
                </span>
              </div>
            )}
          </div>
          <div className={styles.lifestyleQuestion__sponsoredBrand}>
            {sponsoredLogoUrl && sponsoredCompany && (
              <>
                <div className={styles.lifestyleQuestion__sponsoredBrand__text}>
                  Brought to you by
                </div>
                <div className={styles.lifestyleQuestion__sponsoredBrand__logo}>
                  <img
                    src={sponsoredLogoUrl}
                    alt={sponsoredCompany.fields.brandName}
                  />
                </div>
              </>
            )}
          </div>
          <div className={styles.lifestyleQuestion__questionAnswers}>
            {nextQuestion.fields.questionOptions?.map((option) => (
              <Button
                key={`${nextQuestion.sys.id}-${option}`}
                type="secondary"
                className={styles.lifestyleQuestion__answer}
                onClick={() => props.onSelect(nextQuestion.sys.id, option)}
              >
                {option}
              </Button>
            ))}
          </div>
          {props.layout === "stack" && (
            <div className={styles.lifestyleQuestion__skipQuestion}>
              <a
                className={styles.lifestyleQuestion__skipQuestion__link}
                onClick={() => props.onSkipQuestion(nextQuestion.sys.id)}
              >
                Skip this question
              </a>
              <span className={styles.lifestyleQuestion__skipQuestion__help}>
                Why am I seeing this?
                <Tooltip
                  tooltipText={
                    "Investing is personal. By providing TiiCKER with information about yourself, we can find companies that most strongly align with you, in industries that interest you."
                  }
                />
              </span>
            </div>
          )}
        </div>
      </Container>
    </div>
  );
};
